"use client"

import { nanoid } from "nanoid"

import { getTracewayRuntime, initializeTracewayClient } from "@/lib/traceway-breadcrumbs"
import { apiBaseUrl } from "@/lib/workspace-api"
import { useAuthStore } from "@/stores/auth-store"

export type FrontendActivityCategory =
  | "navigation"
  | "auth"
  | "onboarding"
  | "people"
  | "billing"
  | "astrologer"
  | "conversation"
  | "chat"
  | "websocket"
  | "system"

export type FrontendActivityAction =
  | "route_entry"
  | "route_change"
  | "login_start"
  | "login_success"
  | "login_failure"
  | "onboarding_redirect"
  | "onboarding_autostart"
  | "onboarding_dismiss"
  | "person_create_submit"
  | "person_edit_submit"
  | "billing_topup_click"
  | "billing_checkout_open"
  | "billing_checkout_success"
  | "billing_checkout_failure"
  | "astrologer_select"
  | "conversation_create"
  | "conversation_open"
  | "chat_submit_attempt"
  | "chat_submit_accepted"
  | "chat_submit_rejected"
  | "chat_submit_completed"
  | "chat_retry"
  | "chat_cancel"
  | "ws_connect"
  | "ws_open"
  | "ws_close"
  | "ws_error"
  | "ws_message_submit"
  | "system_event"

type FrontendActivityEventRecord = {
  action: FrontendActivityAction
  category: FrontendActivityCategory
  client_event_id: string
  client_timestamp: string
  conversation_id?: string | null
  element_descriptor?: string | null
  metadata?: Record<string, unknown> | null
  outcome?: string | null
  page?: string | null
  route?: string | null
  session_id: string
  subject_id?: string | null
  submitted_content?: string | null
  trace_id?: string | null
  turn_id?: string | null
}

export type FrontendActivityEventInput = {
  action: FrontendActivityAction
  category: FrontendActivityCategory
  conversationId?: string | null
  elementDescriptor?: string | null
  metadata?: Record<string, unknown> | null
  outcome?: string | null
  page?: string | null
  route?: string | null
  subjectId?: string | null
  submittedContent?: string | null
  turnId?: string | null
}

const ACTIVITY_ENDPOINT_PATH = "/v1/activity/events"
const ACTIVITY_FLUSH_INTERVAL_MS = 15_000
const ACTIVITY_QUEUE_LIMIT = 100
const ACTIVITY_SESSION_STORAGE_KEY = "askjyotish.activity.session_id"
const ACTIVITY_TRACE_STORAGE_KEY = "askjyotish.activity.trace_id"

let flushTimer: number | null = null
let flushInFlight: Promise<void> | null = null
let initialized = false
let pendingEvents: FrontendActivityEventRecord[] = []
const UUID_LIKE_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function buildActivityUrl() {
  return `${apiBaseUrl().replace(/\/+$/, "")}${ACTIVITY_ENDPOINT_PATH}`
}

function currentRoute() {
  if (typeof window === "undefined") {
    return "/"
  }
  const search = window.location.search || ""
  return `${window.location.pathname || "/"}${search}`
}

function currentPage() {
  if (typeof window === "undefined") {
    return null
  }
  return window.location.href
}

function fallbackRandomId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  return nanoid()
}

function ensureStoredValue(storageKey: string) {
  if (typeof window === "undefined") {
    return fallbackRandomId()
  }
  try {
    const cached = window.sessionStorage.getItem(storageKey)
    if (cached) {
      return cached
    }
    const nextValue = fallbackRandomId()
    window.sessionStorage.setItem(storageKey, nextValue)
    return nextValue
  } catch {
    return fallbackRandomId()
  }
}

function getActivityCorrelationIds() {
  initializeTracewayClient()
  const runtime = getTracewayRuntime()
  return {
    sessionId: runtime?.sessionId ?? ensureStoredValue(ACTIVITY_SESSION_STORAGE_KEY),
    traceId: runtime?.traceId ?? ensureStoredValue(ACTIVITY_TRACE_STORAGE_KEY),
  }
}

function normalizeUuidLikeId(value: string | null | undefined) {
  if (!value) {
    return null
  }
  return UUID_LIKE_PATTERN.test(value) ? value : null
}

function canSendAuthenticatedActivity() {
  const auth = useAuthStore.getState()
  return auth.hasHydratedFromStorage && auth.status === "authenticated"
}

function scheduleFlush() {
  if (typeof window === "undefined" || flushTimer !== null) {
    return
  }
  flushTimer = window.setTimeout(() => {
    flushTimer = null
    void flushActivityEvents()
  }, ACTIVITY_FLUSH_INTERVAL_MS)
}

function normalizeEvent(input: FrontendActivityEventInput): FrontendActivityEventRecord {
  const correlationIds = getActivityCorrelationIds()
  return {
    action: input.action,
    category: input.category,
    client_event_id: fallbackRandomId(),
    client_timestamp: new Date().toISOString(),
    conversation_id: input.conversationId ?? null,
    element_descriptor: input.elementDescriptor ?? null,
    metadata: input.metadata ?? null,
    outcome: input.outcome ?? null,
    page: input.page ?? currentPage(),
    route: input.route ?? currentRoute(),
    session_id: correlationIds.sessionId,
    subject_id: input.subjectId ?? null,
    submitted_content: input.submittedContent ?? null,
    trace_id: correlationIds.traceId,
    turn_id: normalizeUuidLikeId(input.turnId),
  }
}

function payloadFor(events: FrontendActivityEventRecord[]) {
  return JSON.stringify({ events })
}

function canUseBeacon(url: URL) {
  return (
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    typeof navigator.sendBeacon === "function" &&
    url.origin === window.location.origin
  )
}

async function sendWithFetch(body: string) {
  await fetch(buildActivityUrl(), {
    body,
    cache: "no-store",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    keepalive: true,
    method: "POST",
  })
}

function sendWithBeacon(body: string) {
  const endpoint = new URL(buildActivityUrl(), window.location.origin)
  const blob = new Blob([body], { type: "application/json" })
  return navigator.sendBeacon(endpoint.toString(), blob)
}

export function initializeActivityEventClient() {
  if (initialized || typeof window === "undefined") {
    return
  }
  initialized = true
  initializeTracewayClient()
  scheduleFlush()

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      void flushActivityEvents({ preferBeacon: true })
    }
  })
  window.addEventListener("pagehide", () => {
    void flushActivityEvents({ preferBeacon: true })
  })
}

export function enqueueActivityEvent(input: FrontendActivityEventInput) {
  initializeActivityEventClient()
  if (!canSendAuthenticatedActivity()) {
    return
  }
  pendingEvents.push(normalizeEvent(input))
  if (pendingEvents.length > ACTIVITY_QUEUE_LIMIT) {
    pendingEvents = pendingEvents.slice(-ACTIVITY_QUEUE_LIMIT)
  }
  if (pendingEvents.length >= 20) {
    void flushActivityEvents()
    return
  }
  scheduleFlush()
}

export async function flushActivityEvents(options?: { preferBeacon?: boolean }) {
  if (pendingEvents.length === 0) {
    return
  }
  if (!canSendAuthenticatedActivity()) {
    pendingEvents = []
    return
  }
  if (flushInFlight) {
    await flushInFlight
    return
  }

  const batch = pendingEvents.splice(0, pendingEvents.length)
  const body = payloadFor(batch)

  flushInFlight = (async () => {
    try {
      const endpoint = new URL(buildActivityUrl(), typeof window !== "undefined" ? window.location.origin : "http://localhost")
      if (options?.preferBeacon && typeof window !== "undefined" && canUseBeacon(endpoint)) {
        const sent = sendWithBeacon(body)
        if (!sent) {
          await sendWithFetch(body)
        }
        return
      }
      await sendWithFetch(body)
    } catch {
      pendingEvents = [...batch, ...pendingEvents].slice(-ACTIVITY_QUEUE_LIMIT)
    } finally {
      flushInFlight = null
      if (pendingEvents.length > 0) {
        scheduleFlush()
      }
    }
  })()

  await flushInFlight
}
